import type { EditMode, GlobalEditMode } from "common/unions";
import { isMobile } from "common/globals";
import {
  COLLAB_HELP,
  EDITING_BASICS,
  EDITING_TECHNIQUES,
  KEYBOARD_INPUT,
  TOOL_TOOLTIPS,
  TOOLBAR_SECTIONS,
  TOOLS_HELP,
  getActiveToolDisplay,
  getSelectionHelpText,
  getToolTooltip,
} from "../constants/helpCopy";

const SECTION_HE: Record<keyof typeof TOOLBAR_SECTIONS, string> = {
  import: "ייבוא",
  select: "בחירה",
  draw: "ציור",
  text: "טקסט",
  marks: "ציון",
};

const TOOLTIP_HE: Record<string, string> = {
  FreeText: "תיבת טקסט — גררו מלבן על הלוח",
  annotation: "הערה — לחצו כדי למקם תווית קצרה",
  freeSketch: "שרטוט חופשי — סימנים לא פורמליים בלבד. Esc או לחיצה נוספת כדי לצאת.",
  freeSketchOcr:
    "ציירו סימן; הפסקה קצרה מאחדת קווים (למשל =), ואז הוא מזוהה ומונח על הרשת",
  Line: "קו — ציירו קטע; הוא נשאר נבחר אחרי השחרור",
  polyline: "פוליגון — גררו כל קטע; סגרו על נקודת ההתחלה. Esc או לחיצה נוספת כדי לצאת.",
  DivisionLine: "קו שבר — נשאר נבחר אחרי השחרור",
  curve: "עקומה — גררו קטע; נשארת נבחרת אחרי השחרור",
  circle: "מעגל — נשאר נבחר אחרי השחרור",
  parabola:
    "פרבולה — לחצו על 3 נקודות שהעקומה עוברת דרכן. בטלו נקודה אחרונה כדי לחזור. אחר כך גררו נקודה ממוספרת.",
  hyperbola:
    "היפרבולה — לחצו על המרכז, ואז גררו לקודקוד. ידית עגולה מזיזה; ידיות מרובעות משנות גודל.",
  sqrt: "שורש — מוסיפים בתא הנבחר (Alt+S). בחרו סימנים קודם כדי לצייר את הקו מעליהם",
  exponent: "חזקה — לחצו על תא (Alt+X)",
  log: "לוגריתם (Alt+L)",
  "cartesian system": "צירים קרטזיים — לחצו כדי למקם צירי x ו-y ממוספרים",
  checkmark: "סמנו תא כנכון",
  xmark: "סמנו תא כשגוי",
  semicheckmark: "סמנו תא כנכון חלקית",
};

const STATUS_HE: Partial<Record<EditMode | GlobalEditMode, string>> = {
  FREE_SKETCH_STARTED: "שרטוט חופשי",
  FREE_SKETCH_DRAWING: "שרטוט חופשי",
  FREE_SKETCH_WITH_OCR_STARTED: "שרטוט → סימן",
  FREE_SKETCH_WITH_OCR_DRAWING: "שרטוט → סימן",
  TEXT_STARTED: "תיבת טקסט",
  TEXT_WRITING: "תיבת טקסט",
  SQRT_STARTED: "שורש",
  CURVE_STARTED: "עקומה",
  CURVE_DRAWING: "עקומה",
  EXPONENT_STARTED: "חזקה",
  EXPONENT_WRITING: "חזקה",
  LINE_STARTED: "קו",
  LINE_DRAWING: "קו",
  DIVISIONLINE_STARTED: "קו שבר",
  DIVISIONLINE_DRAWING: "קו שבר",
  CIRCLE_STARTED: "מעגל",
  CIRCLE_DRAWING: "מעגל",
  PARABOLA_STARTED: "פרבולה",
  HYPERBOLA_STARTED: "היפרבולה",
  CONIC_DRAWING: "חתך חרוט",
  ANNOTATION_STARTED: "הערה",
  ANNOTATION_WRITING: "הערה",
  POLYGON_STARTED: "פוליגון",
  POLYGON_DRAWING: "פוליגון",
  CHECKMARK_STARTED: "וי",
  SEMICHECKMARK_STARTED: "וי חלקי",
  XMARK_STARTED: "איקס",
  COLORIZING: "צבע",
  AREA_SELECTION_STARTED: "בחירה",
  LOG_STARTED: "לוג",
  CARTESIAN_SYSTEM_STARTED: "צירים",
};

const HINT_HE: Partial<Record<EditMode | GlobalEditMode, string>> = {
  FREE_SKETCH_STARTED: "ציור לא פורמלי בלבד — למתמטיקה הקלידו במקלדת.",
  FREE_SKETCH_DRAWING: "Esc או לחיצה נוספת על הכלי כדי לצאת.",
  TEXT_STARTED: "גררו מלבן, ואז הקלידו. גררו פינה כדי לשנות גודל.",
  TEXT_WRITING:
    "הקלידו בתיבה. גררו פינה כדי לשנות גודל, ואז לחצו בחוץ. בתרגול, הפסקה מביאה רמז; Ctrl+Enter בודק.",
  LINE_STARTED: "גררו כדי לצייר קו. הקו נשאר נבחר כשמשחררים.",
  LINE_DRAWING: "שחררו כדי למקם. הקו נשאר נבחר.",
  CIRCLE_STARTED: "גררו כדי לצייר מעגל. המעגל נשאר נבחר כשמשחררים.",
  CIRCLE_DRAWING: "שחררו כדי למקם. המעגל נשאר נבחר.",
  DIVISIONLINE_STARTED: "גררו קו שבר אופקי. הוא נשאר נבחר כשמשחררים.",
  DIVISIONLINE_DRAWING: "שחררו כדי למקם. הקו נשאר נבחר.",
  CONIC_DRAWING: "לחצו על הנקודה הממוספרת הבאה, או בטלו את הנקודה האחרונה.",
  PARABOLA_STARTED:
    "לחצו על 3 נקודות שהעקומה עוברת דרכן. בטלו נקודה אחרונה כדי לחזור.",
  HYPERBOLA_STARTED:
    "לחצו על המרכז, ואז גררו לקודקוד. ידית עגולה מזיזה; ידיות מרובעות משנות גודל.",
  ANNOTATION_STARTED: "לחצו כדי למקם טקסט.",
  ANNOTATION_WRITING: "הקלידו תווית קצרה, ואז Enter או לחיצה בחוץ. Esc מבטל.",
  AREA_SELECTION_STARTED: isMobile()
    ? "לחצו על בחירה, גררו מלבן, ואז גררו כדי להזיז."
    : "גררו מלבן כדי לבחור, ואז גררו כדי להזיז. החזיקו Ctrl (Cmd ב-Mac) כדי לשכפל.",
  CURVE_STARTED: "גררו כדי לצייר עקומה. העקומה נשארת נבחרת כשמשחררים.",
  CURVE_DRAWING: "שחררו כדי למקם. העקומה נשארת נבחרת.",
  POLYGON_STARTED: "גררו כל קטע; סגרו על נקודת ההתחלה.",
  POLYGON_DRAWING: "גררו את הקטע הבא, או סגרו על נקודת ההתחלה.",
  CARTESIAN_SYSTEM_STARTED: "לחצו כדי למקם צירי x ו-y ממוספרים. שני הצירים נשארים נבחרים.",
  FREE_SKETCH_WITH_OCR_STARTED:
    "ציירו קווים; הפסקה קצרה מאחדת אותם, ואז הזיהוי מניח את הסימן על הרשת.",
  FREE_SKETCH_WITH_OCR_DRAWING: "שחררו כדי לסיים קו — ציירו עוד בתוך רגע לסימנים מרובי קווים.",
};

export function toolbarSection(
  key: keyof typeof TOOLBAR_SECTIONS,
  he: boolean,
): string {
  return he ? SECTION_HE[key] : TOOLBAR_SECTIONS[key];
}

export function toolTooltip(name: string, he: boolean): string {
  if (!he) return getToolTooltip(name);
  return TOOLTIP_HE[name] ?? TOOL_TOOLTIPS[name] ?? name;
}

export function selectionHelp(he: boolean): string {
  if (!he) return getSelectionHelpText();
  return isMobile()
    ? "לחצו על בחירה, גררו מלבן כדי לבחור, ואז גררו כדי להזיז. לחיצה כפולה יוצאת ממצב בחירה."
    : "גררו מלבן כדי לבחור, ואז גררו כדי להזיז. החזיקו Ctrl (Cmd ב-Mac) בזמן גרירה כדי לשכפל. Delete או Backspace מוחקים את הבחירה.";
}

export function localizedActiveTool(
  mode: EditMode | GlobalEditMode,
  he: boolean,
): { label: string; hint: string } | null {
  const base = getActiveToolDisplay(mode);
  if (!base || !he) return base;
  return {
    label: STATUS_HE[mode] ?? base.label,
    hint: HINT_HE[mode] ?? base.hint,
  };
}

export function keyboardHelp(he: boolean) {
  if (!he) return KEYBOARD_INPUT;
  const mobile = isMobile();
  return {
    title: "מקלדת (מומלץ)",
    intro: "רוב המתמטיקה מוקלדת ישר לתאי הרשת — מהר וברור יותר משרטוט חופשי.",
    tips: mobile
      ? [
          "לחצו על תא, ואז הקלידו אותיות, מספרים ופעולות. ההקלדה מזיזה סימנים ימינה.",
          "השתמשו בכלי הטקסט לחזקות, לוגים ותיבות טקסט.",
          "שרטוט חופשי הוא אופציונלי, לתרשימים גסים בלבד.",
        ]
      : [
          "לחצו על תא, ואז הקלידו אותיות, מספרים ופעולות. ההקלדה מזיזה סימנים ימינה.",
          "רווח זז ימינה אם התאים הבאים ריקים; אחרת הוא דוחף סימנים. Enter יורד אם השורות הבאות ריקות; אחרת הוא דוחף את העבודה למטה.",
          "Backspace או Delete מוחקים את הסימן בתא הנבחר.",
          "Alt+X חזקה, Alt+L לוג, Alt+S שורש — או כלי הטקסט משמאל.",
          "שרטוט חופשי הוא אופציונלי, לא לעבודה העיקרית.",
        ],
  };
}

export function editingTechniquesHelp(he: boolean) {
  if (!he) return EDITING_TECHNIQUES;
  return {
    title: "בחירה, הזזה והעתקה",
    items: isMobile()
      ? [
          "כלי בחירה: גררו מלבן, ואז גררו את הבחירה כדי להזיז.",
          "לחצו על תא והקלידו; השתמשו בסרגל לכלים מובנים.",
        ]
      : [
          "כלי בחירה: גררו מלבן כדי לבחור, ואז גררו כדי להזיז.",
          "החזיקו Ctrl (Cmd ב-Mac) בזמן גרירה כדי לשכפל.",
          "Delete או Backspace מוחקים סימונים נבחרים.",
          "כשסימנים נבחרים, שורש (Alt+S) מצייר את הקו מעליהם.",
          "כשתא נבחר: רווח זז ימינה אם התאים הבאים ריקים, אחרת דוחף; Enter יורד אם השורות הבאות ריקות, אחרת דוחף; Backspace/Delete מנקים את התא.",
          "מקשי החצים מזיזים את התא הנבחר; הקלדה מוסיפה (סימנים קיימים זזים ימינה).",
        ],
  };
}

export function editingBasicsHelp(he: boolean) {
  if (!he) return EDITING_BASICS;
  return {
    idle: isMobile()
      ? "לחצו על תא והקלידו. בחירה מזיזה עבודה; שרטוט חופשי הוא אופציונלי."
      : "לחצו על תא והקלידו. בחירה: גררו כדי לבחור, להזיז, או Ctrl+גרירה כדי להעתיק. קו, מעגל, עקומה וצירים יוצאים אחרי צורה אחת; פוליגון ושרטוט נשארים עד Esc או לחיצה נוספת על הכלי.",
    exitHint: isMobile()
      ? "לחיצה כפולה או לחיצה נוספת על הכלי הפעיל כדי לצאת."
      : "Esc או לחיצה נוספת על הכלי הפעיל כדי לצאת.",
  };
}

export function toolsHelp(he: boolean) {
  if (!he) return TOOLS_HELP;
  return {
    title: "כלי הלוח",
    intro:
      "קו, מעגל, עקומה, חתכי חרוט וצירים ממקמים צורה אחת ומשאירים אותה נבחרת. פוליגון ושרטוט נשארים עד Esc או לחיצה נוספת על אותו כלי.",
    groups: [
      {
        name: "מקלדת",
        tools: [
          "לחצו על תא והקלידו — מוסיף כמו במסמך ומזיז סימנים ימינה",
          "רווח — זז ימינה אם התאים הבאים ריקים, אחרת דוחף סימנים",
          "Enter — יורד אם השורות הבאות ריקות, אחרת דוחף את העבודה",
          "Backspace / Delete — מנקים תא או מוחקים בחירה",
        ],
      },
      {
        name: "בחירה",
        tools: [
          "גררו מלבן כדי לבחור",
          "גררו את הבחירה כדי להזיז",
          "Ctrl+גרירה (Cmd ב-Mac) כדי לשכפל",
          "Delete / Backspace — מוחקים בחירה",
        ],
      },
      {
        name: "ציור",
        tools: [
          "שרטוט חופשי — סימנים לא פורמליים; Esc או לחיצה נוספת כדי לצאת",
          "קו — קטע אחד, ואז הקו נשאר נבחר",
          "פוליגון — נשאר עד שסוגרים, Esc, או לחיצה נוספת על הכלי",
          "עקומה — עקומה אחת, ואז היא נשארת נבחרת",
          "מעגל — מעגל אחד, ואז הוא נשאר נבחר",
          "צירים קרטזיים — שנתות ממוספרות; שני הצירים נשארים נבחרים",
        ],
      },
      {
        name: "טקסט",
        tools: ["תיבת טקסט", "הערה", "שורש", "חזקה", "לוג"],
      },
    ],
  };
}

const COACH_MARK_HE: Record<string, { title: string; body: string }> = {
  "quick-tip-keyboard": {
    title: "הקלידו על הרשת",
    body: "לחצו על תא והקלידו — הדרך המהירה לכתוב מתמטיקה. שרטוט חופשי הוא רק לסימנים לא פורמליים.",
  },
  "quick-tip-selection": {
    title: "הזזה והעתקה",
    body: "בחירה: גררו מלבן כדי לבחור, גררו כדי להזיז, והחזיקו Ctrl (Cmd ב-Mac) בזמן גרירה כדי לשכפל.",
  },
  "quick-tip-special-symbols": {
    title: "סימנים מיוחדים",
    body: "השתמשו בלוח מימין (או בכפתור Σ במובייל) לאותיות יווניות, √, °, × ועוד. הם עובדים בתאים, בתיבות טקסט ובהערות.",
  },
  "quick-tip-shortcuts": {
    title: "קיצורי תא",
    body: "רווח עובר לתא הבא כשהשורה ריקה בהמשך, או דוחף סימנים. Enter יורד כשהשורות הבאות ריקות, או דוחף את העבודה. Backspace או Delete מנקים את התא.",
  },
  "practice-assist-modes": {
    title: "איך העזרה עובדת",
    body: "בדיקת תשובה מדרגת כשלוחצים עליה (Ctrl+Enter, גם בתוך תיבת טקסט). רמזים נותנים רמז כתוב אחרי הפסקה. דיבור מקריא את הרמז. רמזים משתמשים במכסה היומית.",
  },
  "tool-selection": {
    title: "בחירה",
    body: "גררו כדי לבחור, ואז הזיזו. החזיקו Ctrl (Cmd ב-Mac) בזמן גרירה כדי לשכפל. Delete מוחק את הבחירה.",
  },
  "tool-line": {
    title: "כלי קו",
    body: "ציירו קו ישר על הרשת. הוא נשאר נבחר כשמשחררים — לחצו שוב על קו כדי לצייר עוד אחד.",
  },
  "tool-text": {
    title: "תיבת טקסט",
    body: "גררו מלבן על הלוח, ואז הקלידו. בתרגול, הפסקה מביאה רמז; Ctrl+Enter בודק את התשובה.",
  },
  "practice-write-here": {
    title: "הקלידו כאן",
    body: "התא המסומן הוא המקום להתחיל לכתוב. הקלידו במקלדת.",
  },
};

export function coachMarkText(
  id: string | undefined,
  title: string,
  body: string,
  he: boolean,
): { title: string; body: string } {
  if (!he || !id) return { title, body };
  return COACH_MARK_HE[id] ?? { title, body };
}

export function helpChrome(he: boolean) {
  if (!he) {
    return {
      title: "Help",
      close: "Close",
      collaborate: "Collaborate",
      tools: "Tools",
      page: "This page",
      watch: "Video tutorials",
      practicePageTitle: "Practice",
      practicePageLines: [
        "Pick a subject or start a blank sheet.",
        "Write on the board, then check your answer or ask for a tip.",
      ],
      practiceListTitle: "Practice",
      practiceListLines: ["Choose a subject, then open a question."],
      undoPoint: "Undo last point",
      cancel: "Cancel",
      parabola: (count: number) => `Parabola (${count}/3)`,
      parabolaHint: (count: number) => {
        if (count === 0) {
          return "Click 3 points the curve must pass through (intercepts work well). You can undo after each click.";
        }
        if (count === 1) return "Point 1 set. Click point 2 of 3, or Undo last point.";
        return "Point 2 set. Click point 3 of 3. Preview follows the pointer. Undo last point to go back.";
      },
      quickTip: "Quick tip",
      skip: "Skip",
      gotIt: "Got it",
      of: "of",
      watchClip: (seconds: number | null) =>
        seconds ? `Watch (${seconds}s)` : "Watch",
    };
  }
  return {
    title: "עזרה",
    close: "סגירה",
    collaborate: "שיתוף",
    tools: "כלים",
    page: "העמוד הזה",
    watch: "סרטוני הדרכה",
    practicePageTitle: "תרגול",
    practicePageLines: [
      "בחרו נושא או התחילו מדף ריק.",
      "כתבו על הלוח, ואז בדקו את התשובה או בקשו רמז.",
    ],
    practiceListTitle: "תרגול",
    practiceListLines: ["בחרו נושא, ואז פתחו שאלה."],
    undoPoint: "ביטול הנקודה האחרונה",
    cancel: "ביטול",
    parabola: (count: number) => `פרבולה (${count}/3)`,
    parabolaHint: (count: number) => {
      if (count === 0) {
        return "לחצו על 3 נקודות שהעקומה עוברת דרכן. אפשר לבטל אחרי כל לחיצה.";
      }
      if (count === 1) return "נקודה 1 נקבעה. לחצו על נקודה 2 מתוך 3, או בטלו את הנקודה האחרונה.";
      return "נקודה 2 נקבעה. לחצו על נקודה 3 מתוך 3. התצוגה עוקבת אחרי הסמן. בטלו את הנקודה האחרונה כדי לחזור.";
    },
    quickTip: "טיפ קצר",
    skip: "דילוג",
    gotIt: "הבנתי",
    of: "מתוך",
    watchClip: (seconds: number | null) =>
      seconds ? `צפייה (${seconds} שנ׳)` : "צפייה",
  };
}

const SYMBOL_GROUP_HE: Record<string, string> = {
  greek: "אותיות יווניות",
  geometric: "סימנים גאומטריים",
  algebra: "אלגברה",
  comparison: "סימני השוואה",
  sets: "פעולות על קבוצות",
  trig: "פונקציות טריגונומטריות",
  calculus: "פונקציות וחדו״א",
  limits: "גבולות",
  integrals: "אינטגרלים",
  vectors: "וקטורים",
};

const SYMBOL_TIP_HE: Record<string, string> = {
  alpha: "אלפא",
  beta: "בטא",
  gamma: "גמא",
  delta: "דלתא",
  theta: "תטא",
  epsilon: "אפסילון",
  zeta: "זטא",
  omega: "אומגה",
  sigma: "סיגמא",
  phi: "פי",
  pi: "פאי",
  rightAngle: "זווית ישרה (90°)",
  angle: "זווית",
  triangle: "משולש",
  perimeter: "היקף",
  area: "שטח",
  perpendicular: "ניצב",
  parallel: "מקביל",
  fxAlgebra: "הפונקציה f(x)",
  sqrt: "שורש ריבועי (√)",
  cbrt: "שורש שלישי (∛)",
  sup2: "חזקה 2 (²)",
  sup3: "חזקה 3 (³)",
  times: "כפל (×)",
  lt: "קטן מ",
  gt: "גדול מ",
  ne: "לא שווה",
  ge: "גדול או שווה",
  le: "קטן או שווה",
  plusmn: "פלוס־מינוס",
  emptySet: "הקבוצה הריקה",
  union: "איחוד",
  intersection: "חיתוך",
  subset: "תת־קבוצה",
  superset: "קבוצה מכילה",
  difference: "הפרש קבוצות",
  complementA: "המשלים של A",
  complementB: "המשלים של B",
  complementP: "המשלים של P",
  element: "שייך ל־",
  notElement: "לא שייך ל־",
  sin: "סינוס",
  cos: "קוסינוס",
  tan: "טנגנס",
  cot: "קוטנגנס",
  "f(x)": "הפונקציה f(x)",
  "f'(x)": "נגזרת ראשונה",
  "f''(x)": "נגזרת שנייה",
  infin: "אינסוף",
  lim: "גבול",
  int: "אינטגרל",
  vector: "וקטור",
  magnitude: "גודל (נורמה)",
  dot: "מכפלה סקלרית",
  cross: "מכפלה וקטורית",
};

export function symbolGroupTitle(id: string, english: string, he: boolean): string {
  if (!he) return english;
  return SYMBOL_GROUP_HE[id] ?? english;
}

export function symbolTooltip(name: string, english: string, he: boolean): string {
  if (!he) return english;
  return SYMBOL_TIP_HE[name] ?? english;
}

export function symbolChrome(he: boolean) {
  if (!he) {
    return {
      specialSymbols: "Special symbols",
      specialSymbolsTitle: "Special Symbols",
      close: "Close",
      degreeMeasure: "Degree measure",
      degrees: "Degrees",
      degreeTip:
        "Degree label — places the measure as an annotation on the selected cell",
      degreesOf: (d: number) => `${d} degrees`,
      typeSymbol: "Type symbol in selected cell",
      integralFrom: "From (lower bound)",
      integralTo: "To (upper bound)",
      integralInfinityOf: (side: "from" | "to") =>
        side === "from"
          ? "Set from (lower bound) to ∞"
          : "Set to (upper bound) to ∞",
      integralNegInfinityOf: (side: "from" | "to") =>
        side === "from"
          ? "Set from (lower bound) to −∞"
          : "Set to (upper bound) to −∞",
      limApproach: "Approaches (under lim)",
      limPresetOf: (preset: string) => `Set approach to ${preset}`,
    };
  }
  return {
    specialSymbols: "סימנים מיוחדים",
    specialSymbolsTitle: "סימנים מיוחדים",
    close: "סגירה",
    degreeMeasure: "מידת הזווית",
    degrees: "מעלות",
    degreeTip: "תווית מעלות — מניחה את המידה כהערה על התא הנבחר",
    degreesOf: (d: number) => `${d} מעלות`,
    typeSymbol: "הקלידו סימן בתא הנבחר",
    integralFrom: "מ- (גבול תחתון)",
    integralTo: "עד (גבול עליון)",
    integralInfinityOf: (side: "from" | "to") =>
      side === "from" ? "הגדירו את הגבול התחתון ל־∞" : "הגדירו את הגבול העליון ל־∞",
    integralNegInfinityOf: (side: "from" | "to") =>
      side === "from"
        ? "הגדירו את הגבול התחתון ל־−∞"
        : "הגדירו את הגבול העליון ל־−∞",
    limApproach: "שואף ל- (מתחת ל-lim)",
    limPresetOf: (preset: string) => `הגדירו את השאיפה ל־${preset}`,
  };
}

export function uploadTooltip(he: boolean): string {
  return he
    ? "העלאה מהטלפון. כדי להדביק מהלוח, בחרו תא ולחצו Ctrl+V."
    : "Upload from phone. To paste from clipboard, select a cell and press Ctrl+V.";
}

export function collabHelp(isTeacher: boolean, he: boolean): {
  title: string;
  steps: readonly string[];
} {
  if (!he) {
    return isTeacher
      ? { title: COLLAB_HELP.teacherTitle, steps: COLLAB_HELP.teacherSteps }
      : { title: COLLAB_HELP.studentTitle, steps: COLLAB_HELP.studentSteps };
  }
  if (isTeacher) {
    return {
      title: "שיתוף עם תלמידים",
      steps: [
        "השתמשו בהזמנה בסרגל העליון כדי להעתיק את קישור הגישה לשיעור.",
        "התלמידים פותחים את הקישור ומצטרפים ללוח.",
        "פתחו את התלמידים המקוונים כדי לראות מי הצטרף ולאפשר לתלמיד אחד לערוך בכל פעם.",
      ],
    };
  }
  return {
    title: "עבודה עם המורה",
    steps: [
      "אתם צופים בלוח השיעור של המורה.",
      "כשהמורה מאפשר עריכה, לחצו על תא והקלידו במקלדת.",
      "בחירה מזיזה עבודה; Ctrl+גרירה מעתיקה. רווח ו-Delete עורכים את התא הנוכחי.",
      "השינויים מסתנכרנים בזמן אמת לכל מי שבשיעור.",
    ],
  };
}

export function practicePageHelp(
  level: string,
  he: boolean,
): { title: string; lines: string[] } | null {
  if (!he) return null;
  const copy = helpChrome(true);
  if (level === "practice") {
    return { title: copy.practicePageTitle, lines: copy.practicePageLines };
  }
  if (level === "practice-list") {
    return { title: copy.practiceListTitle, lines: copy.practiceListLines };
  }
  return null;
}
